import React from 'react';
import { Grid } from '@mui/material';
import NativeInFeedAd from './NativeInFeedAd';
import { TargetingData } from '../../types/ads';

/**
 * Ad density configuration — AdSense compliance.
 * - AD_FREQUENCY: insert an ad after every N content items (after the no-ad zone)
 * - NO_AD_ZONE: no ads in the first N content items
 * - MAX_AD_RATIO: maximum ratio of ads to total items (ads + content)
 */
const AD_FREQUENCY = 8;
const NO_AD_ZONE = 4;
const MAX_AD_RATIO = 0.12;

interface InFeedAdGridProps {
  children: React.ReactNode[];
  pageContext?: 'home' | 'search' | 'profile';
  targeting?: TargetingData;
  gridItemProps?: {
    xs?: number | boolean;
    sm?: number | boolean;
    md?: number | boolean;
    lg?: number | boolean;
  };
  containerSx?: Record<string, any>;
}

const InFeedAdGrid: React.FC<InFeedAdGridProps> = ({
  children,
  pageContext = 'home',
  targeting = {},
  gridItemProps = { xs: 12, sm: 6, md: 4, lg: 3 },
  containerSx,
}) => {
  const items = React.Children.toArray(children);

  if (items.length === 0) return null;

  const elements: React.ReactNode[] = [];
  let adCount = 0;
  // Track content items placed since last ad (after no-ad zone)
  let contentSinceLastAd = 0;

  // Viewport-aware suppression: no ads if fewer than 3 content items
  const suppressAds = items.length < 3;

  items.forEach((child, index) => {
    elements.push(
      <Grid item {...gridItemProps} key={`outfit-${index}`}>
        {child}
      </Grid>
    );

    // Skip ad insertion in the no-ad zone (first NO_AD_ZONE items)
    if (index < NO_AD_ZONE) return;

    contentSinceLastAd++;

    // Check if we should insert an ad after this item
    if (!suppressAds && contentSinceLastAd >= AD_FREQUENCY) {
      // Check MAX_AD_RATIO before inserting
      const totalAfterAd = elements.length + 1; // +1 for the ad we're about to add
      const adsAfterInsert = adCount + 1;
      if (adsAfterInsert / totalAfterAd <= MAX_AD_RATIO) {
        const currentAdIndex = adCount++;
        contentSinceLastAd = 0;
        elements.push(
          <Grid item {...gridItemProps} key={`ad-${currentAdIndex}`}>
            <NativeInFeedAd
              index={currentAdIndex}
              targeting={{
                ...targeting,
                contextual: {
                  ...targeting.contextual,
                  pageType: pageContext as any,
                },
              }}
              onLoad={() => {
                console.log(`📢 In-feed ad ${currentAdIndex} loaded on ${pageContext} page`);
              }}
              onError={(error) => {
                console.warn(`📢 In-feed ad ${currentAdIndex} error on ${pageContext}:`, error.message);
              }}
            />
          </Grid>
        );
      }
    }
  });

  return (
    <Grid container spacing={containerSx ? 2 : 3} sx={containerSx}>
      {elements}
    </Grid>
  );
};

export default InFeedAdGrid;
